import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Sparkle, Heart, SealCheck, Crown, Star } from "@phosphor-icons/react";
import { mediaUrl } from "@/pages/match/MatchOnboarding";

export default function MatchLikesYou({ onGoPlans }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["match-likes-you"], queryFn: async () => (await api.get("/match/likes-you")).data });

  const like = useMutation({
    mutationFn: async (target_id) => (await api.post("/match/like", { target_id })).data,
    onSuccess: (res) => {
      if (res.matched) toast.success(`It's a match! 🎉 Find ${res.match?.with_name || "them"} in Matches.`, { duration: 5000 });
      else toast.success("Liked back!");
      qc.invalidateQueries({ queryKey: ["match-likes-you"] });
      qc.invalidateQueries({ queryKey: ["match-matches"] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  if (isLoading) return <div className="py-16 text-center text-muted-foreground">Loading…</div>;

  if (data?.gated) {
    return (
      <div className="rounded-3xl border border-rose-200 bg-gradient-to-b from-rose-50 to-card p-8 text-center dark:border-rose-900/40 dark:from-rose-950/30" data-testid="match-likes-gated">
        <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-rose-500 text-white"><Sparkle size={28} weight="fill" /></div>
        <h3 className="font-display text-2xl font-semibold">{data.count} {data.count === 1 ? "person likes" : "people like"} you</h3>
        <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">Upgrade to Plus or higher to see who liked you and match instantly.</p>
        <button data-testid="match-likes-upgrade" onClick={onGoPlans} className="mt-4 inline-flex items-center gap-2 rounded-full bg-rose-500 px-5 py-2.5 font-semibold text-white transition-transform hover:scale-[1.02]">
          <Crown size={17} weight="fill" /> See who likes you
        </button>
      </div>
    );
  }

  const profiles = data?.profiles || [];
  if (!profiles.length) {
    return <div className="rounded-3xl border border-border bg-card p-10 text-center text-sm text-muted-foreground" data-testid="match-likes-empty">No likes yet — keep discovering and they'll show up here.</div>;
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="match-likes-you">
      {profiles.map((p) => (
        <div key={p.user_id} className="overflow-hidden rounded-2xl border border-border bg-card" data-testid={`likes-you-${p.user_id}`}>
          <div className="relative aspect-square bg-secondary">
            {p.photos?.length ? <img src={mediaUrl(p.photos[0].url)} alt={p.display_name} className="h-full w-full object-cover" />
              : <div className="flex h-full items-center justify-center text-4xl font-display text-muted-foreground/40">{p.display_name?.charAt(0)}</div>}
            {p.score === 100 && <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-sky-500 px-2 py-0.5 text-[10px] font-bold text-white"><Star size={10} weight="fill" /> Super Like</span>}
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-3 text-white">
              <p className="flex items-center gap-1.5 font-semibold">{p.display_name}, {p.age}{p.verified && <SealCheck size={14} weight="fill" className="text-sky-400" />}</p>
              {p.city && <p className="text-xs text-white/80">{p.city}{p.country ? `, ${p.country}` : ""}</p>}
            </div>
          </div>
          <button data-testid={`likes-you-like-${p.user_id}`} onClick={() => like.mutate(p.user_id)} disabled={like.isPending}
            className="flex w-full items-center justify-center gap-2 py-3 text-sm font-semibold text-rose-500 transition-colors hover:bg-rose-500/10">
            <Heart size={16} weight="fill" /> Like back
          </button>
        </div>
      ))}
    </div>
  );
}
